/* global modUtils */

const geolocation = {
	defaultLocation: null,
	currentLocation: {},
	cookieExpirationMinutes: 1440,
	locationData: ['country', 'state', 'statecode', 'city', 'zipcode'],
	source: 'maxmind',
	status: '',
	usedSource: null,
	responseCollection: {},

	getGeolocationEndpoint(source = null) {
		if (source === null) {
			source = this.source;
		}

		this.usedSource = source;

		const environment = modUtils.getEnv();

		if (source === 'insurance') {
			return 'https://www.nextinsure.com/listingdisplay/handlers/geo.ashx';
		} else if (source === 'maxmind') {
			const referrer = window.document.location.origin;
			return `https://geoip-js.com/geoip/v2.1/city/me?referrer=${referrer}`;
		}

		// Bing/Google
		const params = modUtils.getUrlParamsToObject();
		const geoConstant = params.geo;
		const domain = environment === 'production' ? 'modernize' : 'hilstaging';
		return `https://api.${domain}.com/v1/geo-targets?type=${source}&geoTargetConstant=${geoConstant}`;
	},

	formatGeoResponse(response) {
		let formattedData = {};

		if (this.usedSource === 'maxmind') {
			formattedData = {
				'country': response.country.names.en,
				'city': response.city.names.en,
				'state': response.subdivisions[0].names.en,
				'statecode': response.subdivisions[0].iso_code,
				'zipcode': response.postal.code
			};
		} else if (this.usedSource === 'google') {
			const googleData = response.data.canonicalName.split(',') || [];
			const googleFormats = {
				'Country': ['country'],
				'State': ['state', 'country'],
				'City': ['city', 'state', 'country'],
				'Postal Code': ['zipcode', 'state', 'country'],
				'Neighborhood-3': ['neighborhood', 'state', 'country'],
				'Neighborhood-4': ['neighborhood', 'city', 'state', 'country']
			};
			const type = (response.data.type === 'Neighborhood') ? (response.data.type + '-' + googleData.length) : response.data.type;
			googleFormats[type].forEach((key, index) => {
				formattedData[key] = googleData[index];
			});
			formattedData.statecode = formattedData.statecode ? formattedData.statecode : response.data.stateCode;
			formattedData.city = formattedData.city ? formattedData.city : response.data.city;
		} else if (this.usedSource === 'bing') {
			const bingData = response.data.name.split('|') || [];
			const bingFormats = {
				'Country': ['country'],
				'State': ['state', 'country'],
				'City': ['city', 'state', 'country'],
				'County': ['county', 'state', 'country'],
				'PostalCode': ['zipcode', 'state', 'country']
			};
			bingFormats[response.data.type].forEach((key, index) => {
				formattedData[key] = bingData[index];
			});
			formattedData.statecode = formattedData.statecode ? formattedData.statecode : response.data.stateCode;
			formattedData.city = formattedData.city ? formattedData.city : response.data.city;
		} else {
			formattedData = response;
		}

		return formattedData;
	},

	fetchUserLocation(onSuccessCallback, onErrorCallback = {}) {
		const _this = this;
		const url = this.getGeolocationEndpoint();
		fetch(url)
			.then(response => response.json())
			.then(data => {
				_this.responseCollection[_this.source] = data;

				// Manipulating data to return a consistent format for all sources
				const formattedResponse = _this.formatGeoResponse(data);

				if (formattedResponse.zipcode) {
					onSuccessCallback(formattedResponse);
				} else {
					_this.source = 'maxmind';
					_this.fetchUserLocation(onSuccessCallback, onErrorCallback);
				}
			})
			.catch(error => {
				if (_this.usedSource === 'google' || _this.usedSource === 'bing') { // Fallback to maxmind source
					_this.source = 'maxmind';
					_this.fetchUserLocation(onSuccessCallback, onErrorCallback);
				} else if (_this.usedSource === 'maxmind') { // Fallback to insurance source
					_this.source = 'insurance';
					_this.fetchUserLocation(onSuccessCallback, onErrorCallback);
				} else {
					onErrorCallback(error);
				}
			});
	},

	getLocationCookie() {
		const cookieValue = modUtils.getCookie('geolocation');
		if (cookieValue) {
			try {
				return JSON.parse(cookieValue);
			} catch (e) {
				return;
			}
		}
		return;
	},

	isValidLocationResponse(data) {
		for (let i = 0; i < this.locationData.length; i++) {
			if (!data[this.locationData[i]]) {
				return false;
			}
		}

		return true;
	},

	setLocationCookie(data) {
		const _this = this;
		const cookieValue = JSON.stringify(data);
		modUtils.setCookie('geolocation', cookieValue, _this.cookieExpirationMinutes);
	},

	setLocationValues(data) {
		if (!this.isValidLocationResponse(data)) {
			return false;
		}

		this.locationData.forEach((key) => {
			this.currentLocation[key] = data[key];
		});
		this.setLocationCookie(this.currentLocation);

		return true;
	},

	populateGeolocationElements(replacementData = null) {
		if (replacementData === null) {
			replacementData = this.currentLocation;
		}

		if (replacementData.country !== 'United States') {
			return;
		}

		document.querySelectorAll('[data-geolocation]:not([data-geolocation-loaded])').forEach((element) => {
			let stringTemplate = element.getAttribute('data-geolocation');
			if (!stringTemplate) {
				return;
			}

			// Replace string template with location data
			Object.keys(replacementData).forEach((key) => {
				if (stringTemplate.includes(`{${key}}`)) {
					const value = replacementData[key] || '';
					stringTemplate = stringTemplate.replace(`{${key}}`, value);
				}
			});

			// Populate the element with the location
			const emptyTags = ['input', 'textarea', 'select'];
			if (emptyTags.indexOf(element.tagName.toLowerCase()) > -1) {
				element.value = stringTemplate;
			} else {
				element.innerHTML = stringTemplate;
			}

			// Mark element as updated
			if (this.status === 'success') {
				element.setAttribute('data-geolocation-loaded', true);
			}
		});
	},

	init(options = {}) {
		const _this = this;
		const params = modUtils.getUrlParamsToObject();

		if (options) {
			Object.assign(this, options);
		}

		if (params.geolocationSrc) {
			_this.source = params.geolocationSrc;
		}

		// Parameter based source override
		if (params.msclkid) {
			_this.source = 'bing';
		} else if (params.gclid) {
			_this.source = 'google';
		}

		const sourceLookup = ['bing', 'google'];

		// Default to maxmind if no geo identifier is present
		if (sourceLookup.includes(_this.source) && !params.geo) {
			_this.source = 'maxmind';
		}

		const addGeoSourceToHeap = function() {
			window.Modalytics.heap('addUserProperties', {
				geoAPIGroup: _this.source
			});
		};

		const onSuccess = (data) => {
			if (_this.setLocationValues(data)) {
				const event = new CustomEvent('geolocation.dataReady', { detail: data });
				document.dispatchEvent(event);
				_this.status = 'success';
				_this.populateGeolocationElements();

				if (window.heap) {
					addGeoSourceToHeap();
				} else {
					document.addEventListener('heap.loaded', function() {
						addGeoSourceToHeap();
					});
				}

				// get maxmind response if source is google/bing
				if (sourceLookup.includes(_this.source)) {
					// Save the currentSource
					const originalSource = _this.source;
					_this.source = 'maxmind';
					_this.fetchUserLocation(() => {
						_this.source = originalSource;
						_this.usedSource = _this.source;
					}, () => {
						_this.source = originalSource;
						_this.usedSource = _this.source;
					});
				}
			}
		};

		const storedGeolocation = this.getLocationCookie();
		if (storedGeolocation && this.isValidLocationResponse(storedGeolocation)) {
			onSuccess(storedGeolocation);
		} else {
			this.fetchUserLocation(onSuccess, function(error) {
				_this.status = 'error';
				const event = new CustomEvent('geolocation.fetchError', { detail: error });
				window.Modalytics.heap('track', ['Geolocation Fetch Failed', { geolocationFailureDetail: error}]);
				document.dispatchEvent(event);
				if (_this.defaultLocation) {
					_this.populateGeolocationElements(_this.defaultLocation);
				}
			});
		}
	},

	ready() {
		const event = new CustomEvent('geolocation.scriptLoaded');
		document.dispatchEvent(event);
	}
};

// If modules are supported, export the geolocation object
if (typeof module !== 'undefined' && typeof module.exports !== 'undefined') {
	module.exports = geolocation;
} else {
	geolocation.ready();
}
